/**
 * Banner doodles — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_doodles`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const bannerDoodlesPack: ToilePack = {
    slug: 'banner-doodles',
    category: 'coloring',
    name: 'Banner doodles',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'ribbons', label: 'Ribbons', subject: 'ribbon banners, a curled ribbon with folded ends, a wavy scroll and a ribbon with forked tails, every one empty inside'},
        {key: 'flags', label: 'Flags and bunting', subject: 'bunting strings, pennant flags and a hanging garland of triangles, every flag empty inside'},
        {key: 'labels', label: 'Labels', subject: 'label shapes, a tag with a string, a stitched rectangle, a scalloped badge and an arrow sign, every one empty inside'},
    ],
    styles: [
        {key: 'fineliner', label: 'Fineliner', style: 'clean fineliner pen, thin even black lines, tidy shapes with small folds and stitches', palette: 'Black ink only, no gray tones.', framing: 'The drawings spread across the whole sheet with wide white gaps between them.'},
        {key: 'double', label: 'Double line', style: 'a double-line outline with a small inner scallop, notebook-doodle style, thin black pen', palette: 'Black ink only, no gray tones.', framing: 'The drawings spread across the whole sheet with wide white gaps between them.'},
        {key: 'wash', label: 'Light wash', style: 'loose black pen outlines under a pale watercolor wash', palette: 'Muted teal and mustard washes inside the outlines, the rest of the sheet white.', framing: 'The drawings spread across the whole sheet with wide white gaps between them.'},
    ],
    directions: ['with stars', 'with flowers', 'red ink'],
    directionHint: 'try: with stars',
    seo: {
        title: 'Banner Doodles: Ribbons, Flags and Labels',
        metaDescription: 'Nine sheets of empty banner doodles, ribbons, flags and labels left blank inside for a heading, in fineliner, double line and wash.',
        h1: 'Banner doodles: ribbons, flags and bunting, labels',
        tagline: 'Empty banners, left blank inside so the title goes in your own hand.',
        intro: 'Banner doodles are hand-drawn ribbons, flags and labels for the headings of notes, posters and project pages. These nine pages are sheets of banners left empty inside, ready for a title. Each comes in fineliner, a double-line outline or a light wash. For a different style, type it in the direction box.',
        useCases: [
            'A heading for a class notebook',
            'A title area on a poster',
            'A header for a project page',
            'A label for a shelf or jar',
        ],
        faq: [
            {q: 'Is there any text on the banners?', a: 'No. Every banner is left blank inside, so the title is written or lettered in afterwards.'},
            {q: 'What is the difference between the three styles?', a: 'Fineliner is clean black pen, the double-line style adds an inner scallop, and the light wash fills the outlines with muted teal and mustard.'},
            {q: 'Can I ask for a different banner style?', a: 'Yes. Type it in the direction box, such as with stars, with flowers or red ink. A typed style is a request, so the result may differ from the samples.'},
        ],
    },
}
